import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../App';

export default function Login() {
  const { login } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot'
  const [form, setForm] = useState({ company_name: '', email: '', password: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const res = await axios.post(endpoint, form);
      login(res.data.token);
    } catch (err) {
      const serverMsg = err.response?.data?.error || '';
      if (serverMsg === 'Invalid credentials') {
        setError('Incorrect email or password. Please try again.');
      } else if (serverMsg === 'Email already registered') {
        setError('An account with this email already exists.');
      } else {
        setError(serverMsg || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    setLoading(true);
    try {
      await axios.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
    } catch {
      setForgotSent(true); // show same message regardless
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: 'var(--accent-blue-dim)',
            border: '1px solid var(--accent-blue)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, marginBottom: 14
          }}>⇄</div>
          <h1 className="page-title" style={{ fontSize: 22, letterSpacing: -0.5 }}>SyncBridge</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
            Procore ↔ QuickBooks Automation
          </p>
        </div>

        <div className="card card-glass" style={{ padding: 28 }}>
          {mode === 'forgot' ? (
            <>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Reset your password</h3>
              {forgotSent ? (
                <div>
                  <div className="alert alert-success" style={{ marginBottom: 16 }}>
                    If an account exists with this email, you'll receive a reset link shortly.
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setMode('login'); setForgotSent(false); setForgotEmail(''); }}>
                    ← Back to sign in
                  </button>
                </div>
              ) : (
                <>
                  <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
                    Enter your email and we'll send you a password reset link.
                  </p>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      className="form-input"
                      type="email"
                      placeholder="you@company.com"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleForgot()}
                    />
                  </div>
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={handleForgot}
                    disabled={loading}
                  >
                    {loading ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Sending...</> : 'Send Reset Link'}
                  </button>
                  <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => setMode('login')}>
                    ← Back to sign in
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              {/* Tabs */}
              <div className="tabs" style={{ marginBottom: 20 }}>
                <button className={`tab ${mode === 'login' ? 'active' : ''}`} onClick={() => { setMode('login'); setError(''); }}>
                  Sign In
                </button>
                <button className={`tab ${mode === 'register' ? 'active' : ''}`} onClick={() => { setMode('register'); setError(''); }}>
                  Register
                </button>
              </div>

              {error && (
                <div className="alert alert-error" style={{ marginBottom: 16 }}>
                  {error}
                </div>
              )}

              {mode === 'register' && (
                <div className="form-group">
                  <label className="form-label">Company Name</label>
                  <input
                    className="form-input"
                    placeholder="Acme Construction"
                    value={form.company_name}
                    onChange={e => setForm({ ...form, company_name: e.target.value })}
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="you@company.com"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  Password
                  {mode === 'login' && (
                    <button
                      onClick={() => { setMode('forgot'); setError(''); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-blue)', fontSize: 12, padding: 0 }}
                    >
                      Forgot password?
                    </button>
                  )}
                </label>
                <input
                  className="form-input"
                  type="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                />
              </div>

              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Loading...</> :
                  mode === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
